import dayjs from 'dayjs';
import {trainingAPI} from '../../api/TrainingAPI';
import { HrColors, distanceColors, TRAFFIC_LIGHT_COLORS, getRestAndTravelDaysInChart, getBarColorsForHighcharts, getRestTravelLabel } from '../../Utils/chart_utils';
import { chartExportableOptions } from "../mapperUtil"
import { getAcwrPlotLines, removeAcwrPlotLines } from "@utils/acwr_utils";
import { drilldownPlayerLabel } from '../drilldownPlayerLabel.js';
import { healthyMean, isInjured } from '../../Utils/injury.mjs';
const getChartKeys = (chartType, wellnessKeys = []) => {
  let maxHrStacksArr = ['maxHrStacks_5', 'maxHrStacks_4', 'maxHrStacks_3', 'maxHrStacks_2', 'maxHrStacks_1'];
  if (wellnessKeys.length > 0) {
    maxHrStacksArr = [];
    wellnessKeys = wellnessKeys.map((wellnessKey, i) => {
      maxHrStacksArr.push(`wellnessStacks_${i + 1}`)
      return `${wellnessKey}_score_contribution`;
    })

  } 
  const keys = {
    maxHrStacks: maxHrStacksArr,
    wellness: wellnessKeys
  };

  return keys[chartType] || [];
};

const getCustomChartKeys = (customChartConfig, chartType) => {
  let customChartKeys = [];
  Object.keys(customChartConfig).forEach( (key) => {
    if(key.startsWith("bar") && key.endsWith("_metric")) {
      customChartKeys.push(customChartConfig[key])
    }
  })

  if (chartType === 'hrZone') {
    customChartKeys = customChartKeys.sort()
  }

  return customChartKeys.reverse()
}

const getColor = (chartType, index, totalKeys, teamName = null) => {
  if (chartType === 'hrZone') {
    return HrColors[totalKeys - index - 1] || HrColors[HrColors.length - 1];
  }
  if (chartType === 'distance') {
    if (teamName === "Campbell University Football") {
      return distanceColors[`distance_${index + 1}`] || distanceColors['distance_1']
    }
    return distanceColors[`distance_${totalKeys - index}`] || distanceColors['distance_1'];
  }
  if (totalKeys === 3) {
    return TRAFFIC_LIGHT_COLORS[totalKeys - index - 1] || TRAFFIC_LIGHT_COLORS[TRAFFIC_LIGHT_COLORS.length - 1];
  }

  return `hsl(${index * 30}, 70%, 50%)`;
};

const getChartTitle = (chartType, customTitle = "") => {
  switch (chartType) {
    case 'hrZone':
      return 'Time in HR Zone';
    case 'wellness':
      return 'Wellness Data';
    default:
      return customTitle || `${chartType.charAt(0).toUpperCase() + chartType.slice(1)}`;
  }
};

const getSeriesName = (chartType, key, index, totalKeys, metrics) => {
  if(chartType === 'hrZone') {
    return `HR Zone ${totalKeys - index}`;
  }

  if(chartType === 'custom') {
    const metricObject = metrics.find( (metric) => metric?.key === key)
    return metricObject?.name || "";
  }

  return `${chartType.charAt(0).toUpperCase() + chartType.slice(1)} ${totalKeys - index}`;
}

const DISTANCE_BAND_PATTERN = /^distance_(\d+)$/;

const resolveFieldValue = (item, key) => {
  const match = key.match(DISTANCE_BAND_PATTERN);
  if (match) {
    const newKey = `velocity_band${match[1]}_total_distance`;
    if (item[newKey] != null) return Number(item[newKey]);
  }
  return Number(item[key]);
};

const getCategory = (chartType, item, games) => {
  const opponent = games?.find((x) => x?.date === item?.date)?.opponent || ''
  return chartType === 'hrZone' || chartType === 'wellness' || chartType === 'custom' || chartType === 'distance' ? (opponent || dayjs(item.date).format('MM-DD')) : dayjs(item.date).format('MM-DD');
}

export const stackedBarChartDtoToModel = ({
  data = [],
  wellnessLabels = [],
  games = [],
  chartType = '',
  teamName = '',
  showPredictions = false,
  showACWR = true,
  isTeamChart = false,
  isBreakdownChart = false,
  showRest = false,
  showTravel = false,
  restAndTravel = null,
  startDate = null,
  endDate = null,
  colors_100 = null,
  session = [], 
  playerGroup = [],
  position = [],
  customChartConfig = null,
  metrics = [],
  acwrThresholdConfig = undefined,
  showTitle = true,
  enableDrillDown = false,
  showHamburger = true,
  selectedPlayerId = null
}) => {
  const injuredInGroup = selectedPlayerId ? () => false : isInjured;

  if (!chartType) {
    console.error('Chart type is not specified');
    return null;
  }

  const keys = chartType === 'wellness' ? wellnessLabels?.map(metric => metric?.key).filter(Boolean) : []; 
  let chartKeys = customChartConfig ? getCustomChartKeys(customChartConfig, chartType) : getChartKeys(chartType, keys);

  if (!chartKeys.length) {
    console.error(`Invalid chart type: ${chartType}`);
    return {}
  }

  if (!Array.isArray(chartKeys) || chartKeys.length === 0) {
    console.error(`Invalid or empty chart keys for chart type: ${chartType}`);
    return null;
  }

  const categories = [];
  const predictionSeries = [];
  const restTravelSeries = [];

  const series = chartKeys.map((key, index) => ({
    name: chartType === 'wellness' 
      ? (wellnessLabels?.[index]?.short || key || `Series ${index + 1}`)
      : getSeriesName(chartType, key, index, chartKeys?.length, metrics),
    data: [],
    color: getColor(chartType, index, chartKeys?.length, teamName),
    visible: !(chartType === 'wellness' && 
      (wellnessLabels?.[index]?.short === 'Wellness Score' || key === 'Wellness Score'))
  }));

  const acwrSeries = [];
  let maxValue = 0;

  if (!Array.isArray(data) || data.length === 0) {
    console.warn('No data provided for chart');
    return createEmptyChartOptions(chartType, chartKeys, showTitle, customChartConfig);
  }

  // Handle rest and travel days
  if ((showRest || showTravel) && chartType === "distance") {
    const metricKey = "distance";
    const restAndTravelDates = getRestAndTravelDaysInChart(
      startDate,
      endDate,
      data,
      restAndTravel,
      showRest,
      showTravel
    );

    const max_value = Math.max(...(data.map((dp) => Number(dp[metricKey]) || 0)));
    const maxPreValue = Math.max(...(data.map((dp) => Number(dp[metricKey + "__pre"]) || 0)));

    data = data.concat(
      restAndTravelDates.map((dateData) => ({
        ...dateData,
        disp_date: dayjs(dateData.date).format("MM-DD"),
        [metricKey]: showPredictions ? Math.max(max_value, maxPreValue) : max_value,
        isRestOrTravel: true,
      }))
    );
  }
  
  if (!isTeamChart) {
    data.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  }

  if (isBreakdownChart) {
    data.sort((a, b) => (Number(b.distance) || 0) - (Number(a.distance) || 0));
  }

  data.forEach((item, index) => {
    if (!item) return; 

    if (isTeamChart) {
      categories.push(item.name || '');
    } else {
      categories.push(getCategory(chartType, item, games) || '');
    }
    
    const drilldownId = isTeamChart ? item.name : categories[index];
    let dailyTotal = 0;
    const isRestOrTravel = item.isRestOrTravel || false;

    chartKeys.forEach((key, index) => {
      const value = resolveFieldValue(item, key) || 0;
      series[index].data.push({
        y: value,
        totalDistance: Number(item.distance) || 0,
        isRestOrTravel: isRestOrTravel,
        custom: { injured: injuredInGroup(item) },
        ...(enableDrillDown && {drilldown: drilldownId})
      });
      dailyTotal += value;
    });

    maxValue = Math.max(maxValue, dailyTotal);
    
    const acwrKey = `${chartType}_ACWR`;
    acwrSeries.push(item[acwrKey] !== undefined ? Number(item[acwrKey]) : null);

    if (chartType === "distance") {
      if (showPredictions) {
        const predictionValue = Number(item["distance_pre"]) || null;
        predictionSeries.push(predictionValue);
      }
      if (showRest || showTravel) {
        const value = isRestOrTravel ? Number(item['distance']) || 0 : 0;
        const restTravelLabel = getRestTravelLabel(item);
        restTravelSeries.push({
          y: value,
          color: getBarColorsForHighcharts(item, "distance", false, null),
          restTravelLabel,
          isRestOrTravel: isRestOrTravel,
          dataLabels: {
            enabled: isRestOrTravel,
            format: '{point.restTravelLabel}',
            rotation: 270,
            color: 'black',
            inside: true,
            verticalAlign: 'middle'
          },
          opacity: 0.5,
        });
      } else {
        restTravelSeries.push(0);
      }
    }
  });

  const maxAcwrValue = Math.max(...acwrSeries.filter(v => v !== null && !isNaN(v)));
  const acwrTickInterval = maxAcwrValue / 5;
  const restTravelDays = data.map(item => item?.isRestOrTravel || false);

  const createDrillUpButton = (chart) => {
    if (chart.drillUpButton) {
      chart.drillUpButton.destroy();
    }

    chart.drillUpButton = chart.renderer.button(
      'Back',
      null,
      null,
      () => {
        if (chart && chart.drilldownLevels && chart.drilldownLevels.length > 0) {
          customDrillUp(chart);
        } else {
          console.error('Unable to drill up: chart or drilldown levels not found');
        }
      },
      {
        theme: {},
        padding: 5
      }
    )
    .attr({
      align: 'left',
      zIndex: 7,
      fill: 'transparent',
      border: 0,
      bottom: 20,
      color: 'blue',
      'stroke-width': 0,
      style: {
        color: 'blue',
        fontWeight: 'bold',
        textDecoration: 'underline',
        fontSize: '14px'
      },
    })
    .add()
    .align({
      align: 'left',
      x: 0,
      y: -60
    }, false, 'plotBox');

    return chart.drillUpButton;
  };

  const customDrillUp = (chart) => {
    if (chart.drilldownLevels && chart.drilldownLevels.length > 0) {
      const previousLevel = chart.drilldownLevels.pop();

      // Remove all existing series
      while (chart.series.length > 0) {
        chart.series[0].remove(false);
      }

      if (chart.drilldownLevels.length === 0) {
        // We're back at the top level, restore original series
        if (chart.originalSeries) {
          chart.originalSeries.forEach(seriesOptions => {
            chart.addSeries(seriesOptions, false);
          });
        }
      } else {
        // Restore series from the previous level
        previousLevel.seriesOptions.forEach(seriesOptions => {
          chart.addSeries(seriesOptions, false);
        });
      }

      // Restore xAxis categories if they exist
      if (previousLevel.xAxis) {
        chart.xAxis[0].setCategories(previousLevel.xAxis.categories, false);
      }

      // Update breadcrumbs
      if (chart.drilldownLevels.length > 0) {
        if (chart.drillUpButton) {
          chart.drillUpButton.attr({
            text: `Back`
          });
        }
      } else {
        if (chart.drillUpButton) {
          // Set a fixed height before redrawing
          chart.update({
            chart: {
              height: '100%' // Set your desired fixed height
            }
          });

          chart.drillUpButton.destroy();
          chart.drillUpButton = null;
        }
      }

      updateYAxisMax(chart);
      chart.redraw();
    }
  };

  let formatter = function() {
    return Number.isInteger(this.value) ? categories[this.value] : this.value;
  };

  let positionsArray = [];
  let acwrPlot = [];
  let options = {
    chart: { 
      type: 'column', 
      zoomType: 'xy',
      events: {
        load: function(e) {
          this.options.custom = this.options.custom || {};
          this.options.custom.title = showTitle ? getChartTitle(chartType, customChartConfig?.title) : '';
        },
        drilldown: function(e) {
          const chart = this;
          const drilldownId = e.point.drilldown;
          this.options.custom = this.options.custom || {};
          if (!chart.drilldownLevels) {
            chart.drilldownLevels = [];
            chart.originalSeries = chart.series.map(s => s.userOptions);
          }

          const handleDrilldown = ({ drilldowns, positions, names}) => {
            const series = drilldowns[drilldownId];
            if (positions) positionsArray = [...positions];
            
            if (series) {
              // Store current state before drilling down
              chart.drilldownLevels.push({
                name: chart.title.textStr,
                level: chart.drilldownLevels.length,
                levelNumber: chart.drilldownLevels.length + 1,
                seriesOptions: chart.series.map(s => s.userOptions),
                xAxis: {
                  ...{categories: (chart.drilldownLevels.length == 0 ? categories : positionsArray)}
                }
              });

              // Remove existing series
              while (chart.series.length > 0) {
                chart.series[0].remove(false);
              }

              if (showACWR && acwrPlot.length === 0) {
                acwrPlot = getACWRPlotLines();
              }
              if (showACWR) {
                removeAcwrPlotLines(chart.yAxis[1], acwrThresholdConfig);
              }


              // Add new series
              series.data.forEach(stack => {
                chart.addSeries(stack, false);
              });

              // Update x-axis categories
              const xAxis = chart.xAxis[0];
              xAxis.setCategories((positions ? positions : (names ? [...names[drilldownId]] : [])), true);
              this.options.custom.currentCategories = (positions ? positions : (names ? [...names[drilldownId]] : []));
              this.options.custom.drilldown = (positions ? "positional" : (names ? "player" : ""));

              // Create or update drillUp button
              chart.drillUpButton = createDrillUpButton(chart);

              chart.redraw();
              updateYAxisMax(chart);
            } else {
              console.error('No drilldown series found for:', drilldownId);
            }
          };

          if (chart.drilldownLevels.length === 0) {
            // Only make API call when drilling down from first to second layer
            const date = data[e.point.index]?.date;
            chart.showLoading('Loading data ...');
            trainingAPI.getDrillDownChartData({ 
              date: date,
              position,
              player_group: playerGroup,
              sessions: session,
              player_id: selectedPlayerId || null,
              ...(chartType === 'wellness' && {
                drilldown_type: 'wellness',
                metric_id: wellnessLabels?.map(label => label.question_id).filter(Boolean)
              })
            })
              .then(response => {
                const responseData = response.player_data;

                chart.drilldownData = responseData;
                const {drilldowns, positions} = createDrilldownStructure(responseData, chartType, drilldownId, chartKeys, metrics, enableDrillDown, wellnessLabels);
                handleDrilldown({drilldowns, positions});

                chart.hideLoading();
              })
              .catch(error => {
                console.error('Error fetching drill-down data:', error);
                chart.hideLoading();
              });
          } else {
            // Use existing data for third layer
            const {drilldowns, names} = createDrilldownStructure(chart.drilldownData, chartType, drilldownId, chartKeys, metrics, enableDrillDown, wellnessLabels);
            handleDrilldown({drilldowns, names});
          }

          if (chart.drilldownLevels.length === 2) {
            chart.update({
              xAxis: {
                labels: {
                  rotation: -45,
                  style: {
                    fontSize: '11px',
                    fontFamily: 'Verdana, sans-serif'
                  }
                }
              }
            });
          }

        },
        drillup: function() {
          const chart = this;
          this.options.custom = this.options.custom || {};
          if (chart.drilldownLevels && chart.drilldownLevels.length > 0) {
            const previousLevel = chart.drilldownLevels.pop();

            this.options.custom.currentCategories = positionsArray;
            this.options.custom.drilldown = "positional";

            // Remove existing series
            while (chart.series.length > 0) {
              chart.series[0].remove(false);
            }

            // Add series from the previous level
            previousLevel.seriesOptions.forEach(seriesOptions => {
              chart.addSeries(seriesOptions, false);
            });

            // Update breadcrumbs
            if (chart.drilldownLevels.length > 0) {
              if (chart.drillUpButton) {
                chart.drillUpButton.attr({
                  text: `Back to ${chart.drilldownLevels[chart.drilldownLevels.length - 1].name}`
                });
              }
            } else {
              if (chart.drillUpButton) {
                chart.drillUpButton.destroy();
                chart.drillUpButton = null;
              }
            }

            if (showACWR) {
              removeAcwrPlotLines(chart.yAxis[1], acwrThresholdConfig);
            }

            chart.redraw();
          }

          if (chart.drilldownLevels.length === 0) {
            chart.drilldownData = null;
            // Restore original series if needed
            if (chart.originalSeries) {
              chart.originalSeries.forEach(seriesOptions => {
                chart.addSeries(seriesOptions, false);
              });

              if (showACWR) {
                removeAcwrPlotLines(chart.yAxis[1], acwrThresholdConfig);
                chart.yAxis[1].update({
                  plotLines: acwrPlot
                });
              }

              this.options.custom.currentCategories = categories;
              this.options.custom.drilldown = "";
              
              chart.redraw();
            }
          }

          updateYAxisMax(chart);

          // Update x-axis labels
          chart.update({
            xAxis: {
              labels: {
                rotation: chart.drilldownLevels.length > 0 ? -45 : 0,
                style: {
                  fontSize: chart.drilldownLevels.length > 0 ? '11px' : '12px',
                  fontFamily: 'Verdana, sans-serif'
                }
              }
            }
          });
        },
      }
    },
    drilldown: {
      breadcrumbs: {
        floating: true,
      },
      activeAxisLabelStyle: {
        cursor: 'default',
        color: 'rgb(51, 51, 51)',
        textDecoration: 'none',
        fontWeight: 500,
        tickmarkPlacement: categories.length > 10 ? 'on' : 'between',
        tickWidth: 1
      },
      activeDataLabelStyle: {
        cursor: 'default',
        color: 'rgb(51, 51, 51)',
        textDecoration: 'none',
      },
      series: []
    },
    title: { text: showTitle ? getChartTitle(chartType, customChartConfig?.title) : '' },
    xAxis: {
      categories,
      gridLineWidth: 2,
      labels: categories.length > 20
        ? {
            rotation: -90,
            overflow: 'justify',
            staggerLines: 1,
            step: 0,
            formatter 
          }
        : categories.length > 10
          ? {
              rotation: -45,
              overflow: 'justify',
              staggerLines: 1,
              step: 0,
              formatter 
            }
          : {
              rotation: 0,
              formatter 
            },
      tickmarkPlacement: categories.length > 10 ? 'on' : 'between',
      tickWidth: 1
    },
    yAxis: [
      {
        title: { text: null },
        stackLabels: {
          enabled: true,         
          formatter: function() {
            if (restTravelDays[this.x]) {
              return ''; 
            }
            
            if (chartType === "wellness") return this.total != null ? this.total.toFixed(2) : '';
            else return this.total != null ? this.total.toFixed(1) : '';
          },
        },
        max: maxValue,
        startOnTick: true,
        endOnTick: true,
      },
      {
        title: { text: null },
        opposite: true,
        min: 0,
        max: chartType === "distance" ? maxValue : maxAcwrValue,
        visible: showACWR,
        startOnTick: true,
        endOnTick: true,
        tickInterval: acwrTickInterval
      }
    ],
    legend: {
      enabled: true,
      verticalAlign: 'top',
      layout: 'horizontal'
    },
    tooltip: { 
      shared: true,
      formatter: function() {
        if (this.points[0].point.isRestOrTravel) {
          return false; 
        }
        let s = '<b>' + this.key + '</b>';
        if (chartType === "wellness") {
          s += '<br/><b>' + 'Score Breakdown' + '</b>';
        }

        this.points.forEach(function(point) {
          s += '<br/>' + point.series.name + ': ' + point.y;
        });
        return s;
      }
    },
    plotOptions: {
      column: {
        borderWidth: 0,
        groupPadding: 0.05,
        pointPadding: 0.05,
        stacking: 'normal',
        dataLabels: {
          enabled: false,
          formatter: function() {
            if (this.point.isRestOrTravel) {
              return ''; 
            }
            return this.point.stackTotal === this.point.totalDistance ? this.point.totalDistance.toFixed(1) : '';
          },
          style: { fontWeight: 'bold', color: 'white', textOutline: 'none', fontSize: '11px' }
        }
      }
    },
    series: series,
    exporting: {
      enabled: showHamburger,
      ...chartExportableOptions
    }
  };

  if (chartType === "distance") {
    if (showPredictions) {
      options = addPredictionConfigToStackedBarChart(options, "Distance", "distance", predictionSeries, series, colors_100);
    }

    if (showRest || showTravel) {
      const restTravelSeriesConfig = {
        name: `Rest/Travel`,
        data: restTravelSeries,
        showInLegend: false
      };
      options.series.push(restTravelSeriesConfig);
    }
  }

  function getACWRPlotLines() {
    return getAcwrPlotLines(acwrThresholdConfig)
  }
  
  if (showACWR && acwrSeries.some(value => value !== null && !isNaN(value))) {
    options.series.push({
      name: 'ACWR',
      type: 'spline',
      yAxis: 1,
      data: acwrSeries,
      color: 'blue',
      marker: { enabled: true, radius: 3 },
      selected: true
    });

    const ACWRPlotLines = getACWRPlotLines();
    acwrPlot = [...ACWRPlotLines];
    options.yAxis[1].plotLines = [...ACWRPlotLines];
  }

  function updateYAxisMax(chart) {
    if (chart && chart.yAxis && chart.yAxis[0]) {
      const seriesCount = chart.series.length;
      const dataLength = chart.series[0].yData.length;

      let maxStackHeight = 0;

      for (let i = 0; i < dataLength; i++) {
        let stackHeight = 0;
        for (let j = 0; j < seriesCount; j++) {
          if (chart.series[j].visible && chart.series[j].yData[i] !== null) {
            stackHeight += chart.series[j].yData[i];
          }
        }
        maxStackHeight = Math.max(maxStackHeight, stackHeight);
      }

      const newMax = maxStackHeight * 1.1; // Add 10% padding
      chart.yAxis[0].setExtremes(0, newMax);
    } else {
      console.error('Unable to update y-axis: chart or yAxis not available');
    }
  }

  function createDrilldownStructure(data, chartType, drilldownId, chartKeys, metrics = [], enableDrillDown = false, wellnessLabels = []) {
    const drilldowns = {};
    const positions = [...new Set(data.map(item => item.player_id__position))];
    let names = {};
  
    const getDrilldownSeriesName = (key, index) => {
      if (chartType === 'wellness' && wellnessLabels?.length > 0) {
        return wellnessLabels[index]?.short || key || `Series ${index + 1}`;
      }
      return getSeriesName(chartType, key, index, chartKeys.length, metrics);
    };
    
    // Second layer: Positions with stacked bars
    drilldowns[drilldownId] = {
      id: drilldownId,
      data: chartKeys.map((key, index) => ({
        name: getDrilldownSeriesName(key, index),
        data: positions.map(position => {
          const positionData = data.filter(item => item.player_id__position === position);
          const avgValue = healthyMean(positionData, item => item[key] || 0, injuredInGroup);
          return {
            name: position,
            y: avgValue === null ? null : Number(avgValue.toFixed(2)),
            ...(enableDrillDown && {drilldown: position})
          };
        }),
        color: getColor(chartType, index, chartKeys.length),
        type: 'column',
        stack: 'position'
      }))
    };

    positions.forEach(position => {
      const positionData = data.filter(item => item.player_id__position === position);
      const players = [...new Set(positionData.map(item => drilldownPlayerLabel(item)))];

      drilldowns[position] = {
        id: position,
        data: chartKeys.map((key, index) => ({
          name: getDrilldownSeriesName(key, index),
          data: players.map(player => {
            const playerData = positionData.find(item => drilldownPlayerLabel(item) === player);

            if (position in names) {
              names[`${position}`].add(player);
            } else {
              names[`${position}`] = new Set([player]);
            }
            
            return {
              name: player,
              y: playerData ? playerData[key] || 0 : 0,
              custom: { injured: injuredInGroup(playerData) }
            };
          }),
          color: getColor(chartType, index, chartKeys.length),
          type: 'column',
          stack: 'player'
        }))
      };
    });
    
    return {drilldowns, positions, names};
  }

  return options;
};

function createEmptyChartOptions(chartType, chartKeys, showTitle = true, customChartConfig = null) {
  return {
    chart: { type: 'column' },
    title: { text: showTitle ? getChartTitle(chartType, customChartConfig?.title) : '' },
    xAxis: { categories: [] },
    yAxis: [{ title: { text: null } }],
    series: chartKeys.map((key, index) => ({
      name: `Series ${index + 1}`,
      data: []
    }))
  };
}


export const addPredictionConfigToStackedBarChart = (options, metricName, metricKey, predictionSeries, series, colors_100) => {
	const predictionSeriesConfig = {
	  name: `${metricName} Prediction`,
	  type: 'column',
	  yAxis: 1,
	  data: predictionSeries.map((y, i) => ({
		y,
		        color: getBarColorsForHighcharts({[metricKey]: y}, metricKey, false, null),
		dataLabels: {
		  enabled: !series[i]?.y && y ? true : false
		}
	  })),
	  opacity: 0.6,
	  zIndex: 0,
	  showInLegend: false
	}

  options.plotOptions.column = {
	  ...options.plotOptions.column,
	  shadow: false,
	  grouping: false
	}
  
	options.series = options.series.map((series) => { 
	  return {
      ...series,
      pointPadding: 0.3,
      pointPlacement: 0,
    }
	})
  
	options.series.push(predictionSeriesConfig)

  
	return options;
  }
